// lib
import { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import { isEqual } from 'lodash'

import { DataContext as SidebarDataContext } from '@aytm/app/stats/page/sidebar/components/context'

import { WEIGHTING_HELP } from '@aytm/app/stats/page/sidebar/components/lib/helps'

import { collator } from '@aytm/app/stats/lib/util/helpers'
import { convertCustomVariables } from '@aytm/app/stats/lib/query/variables'
import containerExpander from '@aytm/lib/layout/container-expander'

// components
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { InputSwitch } from 'primereact/inputswitch'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'
import SidebarPopupWarning from '@aytm/app/stats/page/sidebar/components/SidebarPopupWarning'

// styles
import style from '@aytm/app/stats/page/sidebar/weighting/styles/WeightingPanel.module.scss'

const DEFAULT_WEIGHTING = {
  active: false,
  type: '*',
  items: []
}

const WeightingPanel = ({ survey, model, features, onModeChange, onParamsChange }) => {
  const { sidebarState, sidebarDispatch } = useContext(SidebarDataContext)

  const { mode, reportId, isReportVirtual, isReportXpert } = model

  const [weighting, setWeighting] = useState(sidebarState.weighting || model.weighting || DEFAULT_WEIGHTING)

  const [isDisabled, setIsDisabled] = useState(true)

  const componentRef = useRef(null)
  const containerExpanderRef = useRef(null)

  const dropdownsRef = useRef({})

  const [root, setRoot] = useState(null)

  const setComponentRef = useCallback(node => {
    componentRef.current = node
    setRoot(node?.parentNode ?? null)
  }, [])

  // Keyed on the count, not on `survey.weighting`: the aggregate reducer merges the
  // response's weighting node into the existing one, so the node keeps its identity
  // and a memo keyed on it would never see a new count.
  const undefs = useMemo(
    () => (Object.hasOwn(survey, 'weighting') && survey.weighting.undefs) || 0,
    [survey.weighting?.undefs]
  )

  const isChanged = useMemo(
    () =>
      null !== weighting &&
      null !== model.weighting &&
      weighting.active &&
      model.weighting.active &&
      0 < weighting.items.length &&
      !isEqual(weighting, model.weighting),
    [model.weighting, weighting]
  )

  const customVariables = useMemo(() => convertCustomVariables(survey), [survey.customVariables])
  const customVariablesKeys = useMemo(
    () =>
      Object.keys(customVariables).filter(
        key => 'n' == customVariables[key].type && !['source_survey_id'].includes(key)
      ),
    [customVariables]
  )

  const weightingOptions = useMemo(() => {
    const options = [
      { value: '*', text: 'Choose weighting variable' },
      { value: 'var', text: 'Weight by Custom variable' }
    ]
    return options
  }, [])

  const varOptions = useMemo(() => {
    const options = []
    customVariablesKeys.forEach(value => {
      options.push({
        value: value,
        text: value
      })
    })
    if (
      null !== model.weighting &&
      model.weighting.active &&
      'var' == model.weighting.type &&
      0 < model.weighting.items.length &&
      0 > customVariablesKeys.indexOf(model.weighting.items[0])
    ) {
      options.push({
        value: model.weighting.items[0],
        text: model.weighting.items[0]
      })
    }
    options.sort((a, b) => collator.compare(a.text, b.text))
    return options
  }, [model.weighting, customVariablesKeys])

  const onShowDropdown = useCallback(
    key => {
      if (
        null !== containerExpanderRef.current &&
        null !== dropdownsRef.current &&
        dropdownsRef.current[key] != undefined
      ) {
        containerExpanderRef.current.expand(dropdownsRef.current[key].getOverlay())
      }
    },
    [containerExpanderRef, dropdownsRef]
  )
  const onHideDropdown = useCallback(
    key => {
      if (
        null !== containerExpanderRef.current &&
        null !== dropdownsRef.current &&
        dropdownsRef.current[key] != undefined
      ) {
        containerExpanderRef.current.unexpand(dropdownsRef.current[key].getOverlay())
      }
    },
    [containerExpanderRef, dropdownsRef]
  )

  const onChange = (active, type, items) => {
    const newWeighting = {
      active: active || false,
      type: type || weighting.type || '*',
      items: items || weighting.items || []
    }
    setWeighting(newWeighting)

    if (isEqual(newWeighting, model.weighting || DEFAULT_WEIGHTING)) {
      return
    }

    if (model.weighting && newWeighting.active != model.weighting.active) {
      onParamsChange({ weighting: (!isEqual(newWeighting, DEFAULT_WEIGHTING) && newWeighting) || null })
    } else if (!newWeighting.active) {
      onParamsChange({ weighting: (!isEqual(newWeighting, DEFAULT_WEIGHTING) && newWeighting) || null }, false)
    }
  }

  const onUpdate = active => {
    if (active != undefined) {
      // When turning off, fall back to local `weighting` if `model.weighting` is null —
      // avoids a null-deref if the model was cleared while the panel is still mounted.
      const source = active ? weighting : model.weighting || weighting
      const newWeighting = {
        active: active || false,
        type: source.type || '*',
        items: source.items || []
      }
      setWeighting(newWeighting)

      if (isEqual(newWeighting, model.weighting || DEFAULT_WEIGHTING)) {
        return
      }

      onParamsChange({ weighting: (!isEqual(newWeighting, DEFAULT_WEIGHTING) && newWeighting) || null })
    } else {
      onParamsChange({ weighting: weighting })
    }
  }

  const onSelect = value => {
    if ('*' == value) {
      onChange(false, value, [])
    } else {
      switch (value) {
        case 'var':
          onChange(weighting.active, value, (0 < varOptions.length && [varOptions[0]['value']]) || [])
          break
        default:
          onChange(false, value, [])
          break
      }
    }
  }

  const onVariableSelect = value => {
    onChange(weighting.active, weighting.type, value.split('+'))
  }

  const onCancel = () => {
    onChange(model.weighting.active || false, model.weighting.type || '*', model.weighting.items || [])
  }

  useEffect(() => {
    if (null !== componentRef.current) {
      containerExpanderRef.current = containerExpander(componentRef.current, { margin: 20 })
    }
  }, [componentRef])

  useEffect(() => {
    sidebarDispatch({ type: 'UPDATE_STATE', key: 'weighting', state: { weighting } })
  }, [weighting, sidebarDispatch])

  useEffect(() => {
    if (null !== dropdownsRef.current) {
      delete dropdownsRef.current['var']
    }
  }, [weighting.type])

  useEffect(() => {
    setIsDisabled('*' == weighting.type || !weighting.items?.length || features.stat_weighting.disabled)
  }, [weighting, features.stat_weighting.disabled])

  useEffect(() => {
    setWeighting(model.weighting || DEFAULT_WEIGHTING)
  }, [model.weighting, reportId])

  useEffect(() => {
    if (null === model.weighting) return

    // eslint-disable-next-line unicorn/no-lonely-if
    if ('var' == model.weighting.type) {
      // eslint-disable-next-line unicorn/no-lonely-if
      if (0 < model.weighting.items.length && !customVariablesKeys.includes(model.weighting.items[0])) {
        setWeighting(DEFAULT_WEIGHTING)

        onParamsChange({ weighting: null }, false)
      }
    }
  }, [model.weighting, customVariablesKeys, onParamsChange])

  const renderButtons = (type = 'all') =>
    isChanged ? (
      <div className={`${style.row} ${style['row-buttons']}`}>
        {('all' == type || 'cancel' == type) && (
          <div className="p-fluid p-col-5">
            <Button
              className="p-button-rounded p-button-blue p-button-secondary"
              label="Cancel"
              disabled={features.stat_weighting.disabled}
              onClick={features.stat_weighting.disabled ? null : onCancel}
            />
          </div>
        )}
        {('all' == type || 'apply' == type) && (
          <div className="p-fluid p-col-5">
            <Button
              className="p-button-rounded p-button-blue"
              label="Apply"
              disabled={features.stat_weighting.disabled}
              // Called with no argument on purpose: `onUpdate`'s first parameter is the
              // active flag, and a click event is not undefined -- passing the handler
              // by reference would commit the event itself as `active`.
              onClick={features.stat_weighting.disabled ? null : () => onUpdate()}
            />
          </div>
        )}
      </div>
    ) : null

  return (
    <>
      <SidebarPopupWarning
        features={[features['stat_weighting']]}
        isSpecial={'charts' != mode}
        isReportVirtual={isReportVirtual}
        isReportXpert={isReportXpert}
        onModeChange={onModeChange}
      />

      <div ref={setComponentRef} className={style.component}>
        <div className={style['component-title']}>
          Weighting
          {root && <SidebarPopup id="weighting" root={root} help={WEIGHTING_HELP} width="300px" />}
        </div>

        <div className={`${style.row} ${style['row-switch']}`}>
          <InputSwitch
            id="stat-weighting-switch"
            data-testid="stat-weighting-switch"
            className="stat-weighting-switch -dtr-stat-weighting-switch p-inputswitch-narrow"
            checked={weighting.active}
            disabled={isDisabled}
            onChange={isDisabled ? null : event => onUpdate(event.value)}
          />
          <span className={`${style['switch-note']}${weighting.active ? ' ' + style['switch-note-active'] : ''}`}>
            {/* one caption for every kind until the wording is settled; a saved census weighting
                read "Weighted by" with nothing after it */}
            {weighting.active ? 'Weighted by custom variable' : 'No weighting applied'}
          </span>
        </div>
        <div className={style.row}>
          <div className="p-fluid p-col-12">
            <Dropdown
              ref={element => (dropdownsRef.current['type'] = element)}
              data-testid="stat-weighting-type"
              className="p-dropdown-rounded"
              placeholder="Choose weighting options"
              value={weighting.type}
              options={weightingOptions}
              optionValue="value"
              optionLabel="text"
              fixedDropdownDir="bottom"
              disabled={features.stat_weighting.disabled}
              onChange={features.stat_weighting.disabled ? null : event => onSelect(event.value)}
              onShow={features.stat_weighting.disabled ? null : () => onShowDropdown('type')}
              onHide={features.stat_weighting.disabled ? null : () => onHideDropdown('type')}
            />
          </div>
        </div>
        {'var' == weighting.type &&
          (0 < varOptions.length ? (
            <>
              <div className={style.row}>
                <div className="p-fluid p-col-12">
                  <Dropdown
                    ref={element => (dropdownsRef.current['var'] = element)}
                    data-testid="stat-weighting-var"
                    className="p-dropdown-rounded"
                    placeholder="Choose custom variable options"
                    value={weighting.items.join('+')}
                    options={varOptions}
                    optionValue="value"
                    optionLabel="text"
                    fixedDropdownDir="bottom"
                    disabled={features.stat_weighting.disabled}
                    onChange={features.stat_weighting.disabled ? null : event => onVariableSelect(event.value)}
                    onShow={features.stat_weighting.disabled ? null : () => onShowDropdown('var')}
                    onHide={features.stat_weighting.disabled ? null : () => onHideDropdown('var')}
                  />
                </div>
              </div>
              {renderButtons()}
              {/* only a custom-variable weighting can lose its variable; a census one's items are preset parts */}
              {null !== model.weighting &&
                'var' == model.weighting.type &&
                0 < model.weighting.items.length &&
                0 > customVariablesKeys.indexOf(model.weighting.items[0]) && (
                  <div className={style.row}>
                    <div className={style['row-note']}>Weighting wasn&apos;t applied</div>
                  </div>
                )}
              {0 < undefs && null !== model.weighting && model.weighting.active && 0 < model.weighting.items.length && (
                <div className={style.row}>
                  <div className={`${style['row-note']} ${style['row-note-warning']}`}>
                    Warning: some respondents don&apos;t have weights assigned. Weight of 1.0 was assumed for them.
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Cancel scaffold kept for a future reachable empty-state revert (currently inert) */}
              {renderButtons('cancel')}
              <div className={style.row}>
                <div className={style['row-note']}>
                  You don&apos;t seem to have any custom variables. To use custom variables for weighting, upload them
                  on this Results page. Open Filters, then scroll to Uploaded Variables.
                  <br />
                  <a
                    href="https://helpcenter.aytm.com/hc/en-us/articles/360048760532-Custom-Variable-Upload"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Learn more &raquo;
                  </a>
                </div>
              </div>
            </>
          ))}
      </div>
    </>
  )
}

WeightingPanel.propTypes = {
  root: PropTypes.any,
  survey: PropTypes.object,
  model: PropTypes.object,
  features: PropTypes.object,
  onModeChange: PropTypes.func,
  onParamsChange: PropTypes.func
}

WeightingPanel.displayName = 'WeightingPanel'

export default WeightingPanel
